export type HierarchyLeaf = {
  key: string;
  label: string;
  screenCount?: number;
  icon?: string;
};

export type HierarchyMidNode = HierarchyLeaf & {
  subVenueTypes?: HierarchyLeaf[];
  subVehicleTypes?: HierarchyLeaf[];
};

export type HierarchyLocationType = {
  key: string;
  label: string;
  count?: number;
  screenCount?: number;
  venueTypes?: HierarchyMidNode[];
  vehicleTypes?: HierarchyMidNode[];
};

export type VenueHierarchy = {
  locationTypes?: HierarchyLocationType[];
  totalScreens?: number;
};

export type HierarchyChip = {
  key: string;
  label: string;
  count?: number;
  icon?: string;
};

export function getMidOptions(
  location?: HierarchyLocationType | null,
): HierarchyMidNode[] {
  return location?.venueTypes || location?.vehicleTypes || [];
}

export function getSubOptions(
  mid?: HierarchyMidNode | null,
): HierarchyLeaf[] {
  return mid?.subVenueTypes || mid?.subVehicleTypes || [];
}

export function getMidLabel(location?: HierarchyLocationType | null) {
  if (location?.vehicleTypes?.length) return 'Vehicle Types';
  return 'Venue Types';
}

export function getSubLabel(mid?: HierarchyMidNode | null) {
  if (mid?.subVehicleTypes?.length) return 'Sub Vehicle Types';
  return 'Sub Venue Types';
}

export function findLocationType(
  hierarchy: VenueHierarchy | undefined,
  locationType?: string,
) {
  if (!locationType) return undefined;
  return hierarchy?.locationTypes?.find(
    (item) => item.key === locationType || item.label === locationType,
  );
}

export function findLocationTypes(
  hierarchy: VenueHierarchy | undefined,
  locationTypes?: string[],
) {
  if (!locationTypes?.length) return [];
  return locationTypes
    .map((key) => findLocationType(hierarchy, key))
    .filter((item): item is HierarchyLocationType => Boolean(item));
}

export function findMidOption(
  location?: HierarchyLocationType | null,
  midKey?: string,
) {
  if (!midKey) return undefined;
  return getMidOptions(location).find(
    (item) => item.key === midKey || item.label === midKey,
  );
}

function uniqueByKey<T extends { key: string }>(items: T[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
}

export function getMergedMidOptions(locations: HierarchyLocationType[]) {
  return uniqueByKey(locations.flatMap((location) => getMidOptions(location)));
}

export function getMergedSubOptions(mids: HierarchyMidNode[]) {
  return uniqueByKey(mids.flatMap((mid) => getSubOptions(mid)));
}

export function findMidOptions(
  locations: HierarchyLocationType[],
  midKeys?: string[],
) {
  if (!midKeys?.length) return [];
  const mids = getMergedMidOptions(locations);
  return mids.filter(
    (item) => midKeys.includes(item.key) || midKeys.includes(item.label),
  );
}

export function getHierarchyTotalScreens(hierarchy?: VenueHierarchy) {
  if (hierarchy?.totalScreens != null) return hierarchy.totalScreens;

  const locations = hierarchy?.locationTypes || [];
  let total = 0;
  let hasCount = false;

  for (const location of locations) {
    for (const mid of getMidOptions(location)) {
      for (const sub of getSubOptions(mid)) {
        if (typeof sub.screenCount === 'number') {
          total += sub.screenCount;
          hasCount = true;
        }
      }
    }
  }

  return hasCount ? total : undefined;
}

export function toChip(option: {
  key?: string;
  label?: string;
  screenCount?: number;
  count?: number;
  icon?: string;
}): HierarchyChip {
  return {
    key: option.key || option.label || '',
    label: option.label || option.key || '',
    count: option.screenCount ?? option.count,
    icon: option.icon,
  };
}
